import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { CompanyResponse, CompanyId } from '@business/entities/companies';
import { handleMessage, requestMessage } from '@business/messages';
import { CompanyAPI } from '@business/api/company';
import { Session } from '@modules/auth/session';
import { R } from '@core/utils/r';
import { FetchLibraryFoldersQuery, FormLibraryFolderResponse, FormLibraryItemShareItem, FormLibraryItemType } from '@business/entities/formsLibrary';
import { FormLibraryAPI } from '@business/api/form_library';
import { FormLibraryFolderFormData } from '@modules/Forms/AddEditFormLibraryFolder/AddEditFormLibraryFolderModal';
import { MoveFormData } from '@modules/Forms/MoveFormToSharedModal/MoveFormToSharedModal';
import { ItemId } from '@business/entities/library';

export class FormLibraryBloc extends BaseBloc {
  private readonly libraryRepo = new Repository<FormLibraryFolderResponse>({
    getItemId: company => company.id,
  });

  readonly folders$ = this.libraryRepo.items$;
  readonly folders = this.libraryRepo.items;
  readonly selectFolder = this.libraryRepo.selectItem;

  onReset() {
    this.libraryRepo.reset();
  }

  fetchFolders = (params: FetchLibraryFoldersQuery) => {
    return FormLibraryAPI.fetchFolders(params).pipe(this.libraryRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_files_error') }));
  };

  fetchFolderItems = (params: FetchLibraryFoldersQuery) => {
    return FormLibraryAPI.fetchFolderItems(params).pipe(handleMessage({ error: requestMessage('fetch_files_error') }));
  };

  createFolder = (data: FormLibraryFolderFormData) => {
    return FormLibraryAPI.createFolder(data).pipe(
      this.libraryRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_folder'),
      }),
    );
  };

  updateFolder = (id: string, data: FormLibraryFolderFormData) => {
    return FormLibraryAPI.udpateFolder(id, data).pipe(
      this.libraryRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('rename_folder'),
      }),
    );
  };

  moveForm = (data: MoveFormData) => {
    return FormLibraryAPI.moveForm(data).pipe(
      handleMessage({
        type: requestMessage('update_folder_item'),
      }),
    );
  };

  deleteFolder = (id: string) => {
    return showDeleteConfirmation('Delete Folder', 'Do you really want to remove this folder?').pipe(
      switchMap(() => FormLibraryAPI.removeFolder(id)),
      handleMessage({
        type: requestMessage('update_folder_item'),
      }),
    );
  };

  deleteItem = (id: string) => {
    return showDeleteConfirmation('Delete Form', 'Do you really want to remove this form from this folder?').pipe(
      switchMap(() => FormLibraryAPI.removeForm(id)),
      handleMessage({
        type: requestMessage('update_folder_item'),
      }),
    );
  };

  shareItem = (id: ItemId, items: FormLibraryItemShareItem[] = [], type: FormLibraryItemType = FormLibraryItemType.form) => {
    return FormLibraryAPI.shareItem(id, items, type).pipe(
      // this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_folder_item'),
      }),
    );
  };
}
